import { ViewIds } from '@ardoq/api-types';
import { returnNull } from '@ardoq/common-helpers';
import {
  DependencyMapVisualization,
  URL_FIELD_VALUES_ATTR,
  VIEW_NODE_ID_ATTR,
  collapsedReferenceAddressPopover,
  toDependencyMapDegreesState,
} from '@ardoq/dependency-map';
import { ErrorInfoBox } from '@ardoq/error-info-box';
import { COMPONENT_ID_ATTRIBUTE } from '@ardoq/global-consts';
import { ViewLegendContainer } from '@ardoq/graph';
import { useOnMount } from '@ardoq/hooks';
import { Team } from '@ardoq/profiling';
import { usePerformanceTracking } from 'tabview/graphComponent/usePerformanceTracking';
import { text2 } from '@ardoq/typography';
import { ViewLegend } from '@ardoq/view-legend';
import { DisabledZoomControls } from '@ardoq/view-settings';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getAdditionalRowsForGraphViewLegend } from 'tabview/graphViews/graphViewLegend/graphViewLegendUtils';
import { urlFieldValuesPopover } from 'tabview/graphViews/urlFieldValuesPopover';
import { recordViewScrollbarWidth } from 'tabview/recordViewScrollbarWidth';
import { WithLoadingIndicator } from 'tabview/WithLoadingIndicator';
import { useViewLegendSubscription } from 'views/viewLegend/useViewLegendSubscription';
import { DependencyMapViewProps } from './types';
import { t } from '@ardoq/i18n';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';

const VIEW_ID = ViewIds.DEPENDENCY_MAP_2;

const createDependencyMapPopoverResolver =
  (
    resolveCollapsedReferenceAddress: (element: Element | null) => ReactNode,
    resolveUrlFieldValues: (element: Element | null) => ReactNode
  ) =>
  (element: HTMLElement | SVGElement): ReactNode | null => {
    if (element.hasAttribute(URL_FIELD_VALUES_ATTR)) {
      return resolveUrlFieldValues(element);
    }
    if (element.hasAttribute(VIEW_NODE_ID_ATTR)) {
      return resolveCollapsedReferenceAddress(element);
    }
    return null;
  };

const Container = styled.div`
  ${text2};
  line-height: inherit;
  font-size: inherit;

  position: relative;
  height: 100%;
  width: 100%;

  flex: 1;
  overflow: hidden;

  &.forExport {
    overflow: visible;
  }
`;

export const DependencyMapView = ({
  viewSettings,
  viewModel,
  viewInstanceId,
  hoveredComponentId,
  focusedComponentId,
  commands,
  contextMenuName,
  isPresentationMode,
  selectedComponentId = null,
  performanceTrackingId,
  isNewConditionalFormatting,
  containerRef: externalContainerRef,
  activeConditionalFormattingForLegend,
  errorInfoBoxLabels,
  i18nLocale,
  isUserDefinedGrouping,
  dataWorkToken,
  viewStatusReporter,
}: DependencyMapViewProps) => {
  usePerformanceTracking('dependency map view render', 5000, {
    viewId: VIEW_ID,
    team: Team.INSIGHT,
    metadata: {
      itemCount: viewModel.itemCount,
    },
  });
  const heightOffset = useViewLegendSubscription();

  useEffect(() => {
    if (dataWorkToken) {
      viewStatusReporter.reportWorkFinished(dataWorkToken);
    }
  }, [viewStatusReporter, dataWorkToken]);
  const { isLegendActive, showVertical, colorByReference } = viewSettings;
  const {
    nodes,
    componentTypes,
    legendReferenceTypes,
    errors,
    hasClones,
    noConnectedComponents,
    nodeAddressMap,
    hasNonComponentNodes,
    urlFieldValuesByComponentId,
    urlFieldValuesByReferenceId,
  } = viewModel;
  const { hasCollapsedNodes, hasReferenceParents } =
    toDependencyMapDegreesState({ viewSettings, viewModel });
  const localContainerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = externalContainerRef ?? localContainerRef;
  const nodesContainerRef = useRef<HTMLDivElement | null>(null);
  const [clearedErrors, setClearedErrors] = useState(false);
  const [clearedHasClones, setClearedHasClones] = useState(false);
  const [neverShowAgain, setNeverShowAgainState] = useState(false);
  const { getNeverShowAgainUserSetting } = commands;

  useEffect(() => {
    getNeverShowAgainUserSetting().then(setNeverShowAgainState);
  }, [getNeverShowAgainUserSetting]);

  const toggleNeverShowAgain = () => {
    commands.setNeverShowAgainUserSetting(!neverShowAgain);
    setNeverShowAgainState(!neverShowAgain);
  };
  const resolvePopoverContent = createDependencyMapPopoverResolver(
    collapsedReferenceAddressPopover({
      nodeAddressMap,
      isUserDefinedGrouping,
      labels: {
        groupTitle: t(
          i18nLocale,
          'tabview:dependencyMap.collapsedGroupDepth',
          'Collapsed group depth'
        ),
        hierarchyTitle: t(
          i18nLocale,
          'tabview:dependencyMap.collapsedComponentHierarchyDepth',
          'Collapsed component hierarchy depth'
        ),
      },
    }),
    urlFieldValuesPopover({
      urlFieldValuesByComponentId,
      urlFieldValuesByReferenceId,
    })
  );

  useOnMount(() => {
    if (!containerRef.current || !nodesContainerRef.current) {
      return;
    }
    const observer = recordViewScrollbarWidth(
      containerRef.current,
      nodesContainerRef.current
    );
    return () => observer.disconnect();
  });

  useEffect(() => {
    commands.updateViewContext({
      getContainer: () => containerRef.current,
      getGraphComponent: returnNull,
      optimizeLayout: null,
      zoomIn: null,
      zoomOut: null,
      toggleLegend: () => commands.updateViewLegendSettings(!isLegendActive),
      isLegendActive: Boolean(isLegendActive),
      zoomControls: null,
      zoomControlsDisabledState: DisabledZoomControls.NONE,
      viewInstanceId,
    });
  });

  const { setHoveredItem, setFocusedItem } = commands;

  const setHoveredComponentId = useCallback(
    (itemId: string | null) => setHoveredItem(itemId, viewInstanceId),
    [setHoveredItem, viewInstanceId]
  );

  const setFocusedComponentId = useCallback(
    (itemId: string | null) => setFocusedItem(itemId, viewInstanceId),
    [setFocusedItem, viewInstanceId]
  );

  useEffect(() => {
    if (performanceTrackingId) {
      commands.recordRenderComplete(performanceTrackingId);
    }
  }, [performanceTrackingId, nodes, commands]);

  const additionalRows = getAdditionalRowsForGraphViewLegend({
    componentTypes,
    referenceTypes: legendReferenceTypes,
    hasCollapsedNodes,
    hasReferenceParents,
    hasNonComponentNodes,
    isUserDefinedGrouping,
    showComponentSwatches: !colorByReference,
    activeConditionalFormattingForLegend: {
      components: activeConditionalFormattingForLegend.components,
      tags: activeConditionalFormattingForLegend.tags,
      references: colorByReference
        ? activeConditionalFormattingForLegend.references
        : [],
    },
    showComponentShapes: true,
    showReferencesAsLines: false,
    selectableItems: false,
    isNewConditionalFormatting,
    i18nLocale,
  });

  return (
    <WithLoadingIndicator
      viewInstanceId={viewInstanceId}
      loadingSource="activeViewsStatus"
      showContentWhileLoading
      i18nLocale={i18nLocale}
    >
      <Container
        ref={containerRef}
        style={{ display: noConnectedComponents ? 'flex' : undefined }}
        data-context-menu={contextMenuName}
      >
        <DependencyMapVisualization
          viewInstanceId={viewInstanceId}
          nodes={nodes}
          colorByReference={colorByReference}
          selectedComponentId={selectedComponentId}
          hoveredComponentId={hoveredComponentId}
          focusedComponentId={focusedComponentId}
          setHoveredComponentId={setHoveredComponentId}
          setFocusedComponentId={setFocusedComponentId}
          showVertical={showVertical}
          onComponentClick={commands.updateDetailsDrawer}
          onComponentDoubleClick={commands.selectComponent}
          onUrlIconClick={commands.openSingleUrlFieldValue}
          urlFieldValuesByComponentId={urlFieldValuesByComponentId}
          urlFieldValuesByReferenceId={urlFieldValuesByReferenceId}
          contextMenuDataAttribute={COMPONENT_ID_ATTRIBUTE}
          nodesContainerRef={nodesContainerRef}
          onContainerClick={() => setFocusedComponentId(null)}
          isNewConditionalFormatting={isNewConditionalFormatting}
          ariaLabel={t(
            i18nLocale,
            'tabview:dependencyMap.ariaLabel',
            'Dependency map'
          )}
          resolveExtraContent={resolvePopoverContent}
        />

        <ErrorInfoBox
          errors={clearedErrors ? [] : errors}
          hasClones={!neverShowAgain && !clearedHasClones && hasClones}
          clearHasClones={() => setClearedHasClones(true)}
          clearErrors={() => setClearedErrors(true)}
          isShowNeverAgainSet={neverShowAgain}
          isPresentationMode={isPresentationMode}
          toggleNeverShowAgain={toggleNeverShowAgain}
          {...errorInfoBoxLabels}
        />
        <ViewLegendContainer
          heightOffset={heightOffset}
          visible={isLegendActive}
        >
          <ViewLegend
            componentTypes={[]}
            referenceTypes={[]}
            additionalRows={additionalRows}
            activeDiffMode={null}
            labels={getViewLegendLabels(i18nLocale)}
          />
        </ViewLegendContainer>
      </Container>
    </WithLoadingIndicator>
  );
};
